import type { AdminWorkspace, PublicWorkspace } from '../types'
import { STANDARD_CASE_STUDY_ASSETS, STANDARD_PRODUCT_ASSETS } from './standard-content'

const BAY_PATH_PENDING_COLLATERAL = {
  id: 'collateral-employer',
  sectionKey: 'collateral' as const,
  type: 'pdf' as const,
  title: 'Driving Outcomes, Not Waiting for Them',
  description: 'A proactive employer outreach model for Bay Path.',
  url: '/bay-path-employer-agent.png',
  source: 'generated' as const,
  metadata: { thumbnail: '/bay-path-employer-agent.png' },
}

const publicWorkspace: PublicWorkspace = {
  id: '2d145f25-bd0f-430c-8a87-62c18d07d6aa',
  publicId: 'bay-path-preview',
  accountName: 'Bay Path University',
  accountLogoUrl: '/bay-path-logo.png',
  customerSegment: 'university',
  phase: 'sales',
  pricingModel: 'enrollment_roi',
  pricingModes: 'auto',
  firstCallRecorded: false,
  overview: 'A shared plan to give every student proactive career support, increase team capacity, and turn outcomes into proof Bay Path can use.',
  recap: 'Bay Path is bringing career support into a larger, combined institution while serving more online and adult students. Prentus would work alongside the Bay Path team to redesign that system, configure the platform around it, launch with students and staff, and keep improving the results.',
  currentNextStep: 'Review the tailored business case and confirm who should join the proposal conversation.',
  precallOnepager: null,
  successContent: null,
  projects: [],
  renewal: null,
  orderForms: [],
  prequalificationOnly: false,
  businessCase: [
    { id: 'bc-1', body: 'Reach online and adult students with useful career guidance outside office hours.' },
    { id: 'bc-2', body: 'Free the career team from repetitive resume, cover letter, and interview-prep work.' },
    { id: 'bc-3', body: 'Move employer engagement from passive job-board activity to proactive relationship building.' },
    { id: 'bc-4', body: 'Connect student activity to the retention and outcomes evidence leadership needs.' },
  ],
  members: [
    {
      id: 'member-peter',
      side: 'customer',
      name: 'Peter Testori, Ed.D.',
      title: 'Associate VP, Student Engagement & Academic Resources',
      email: null,
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'member-frank',
      side: 'customer',
      name: 'Frank Rojas, PhD',
      title: 'VP of Strategy',
      email: null,
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'member-natalia',
      side: 'customer',
      name: 'Natalia Blank',
      title: 'Chief Financial Officer',
      email: null,
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'member-rod',
      side: 'prentus',
      name: 'Rod Danan',
      title: 'Founder & CEO, Prentus',
      email: 'rod.danan@prentus.com',
      bookingUrl: 'https://cal.com/rod-danan',
      avatarUrl: '/images/team/rod-danan.webp',
    },
    {
      // No name on file yet: the worker falls back to the email local part
      // (never a blank, never the full raw address) until an admin fills it in.
      id: 'member-jhale',
      side: 'customer',
      name: 'jhale',
      title: null,
      email: 'jhale@baypath.edu',
      bookingUrl: null,
      avatarUrl: null,
    },
  ],
  tasks: [
    { id: 'task-1', title: 'Review tailored ROI and partnership options', description: null, ownerName: 'Bay Path + Prentus', dueDate: '2026-08-21', status: 'in_progress' },
    { id: 'task-2', title: 'Confirm the proposal buying committee', description: 'Include academic, finance, and AI strategy leadership.', ownerName: 'Peter', dueDate: '2026-08-24', status: 'open' },
    { id: 'task-3', title: 'Hold proposal and implementation working session', description: null, ownerName: 'Rod', dueDate: '2026-08-28', status: 'open' },
    { id: 'task-4', title: 'Complete security and procurement review', description: null, ownerName: 'Bay Path + Prentus', dueDate: '2026-09-11', status: 'open' },
  ],
  assets: [
    {
      id: 'recording-june-11',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Bay Path University and Prentus demo',
      description: 'June 11, 2026. Product walkthrough and partnership discussion.',
      url: 'https://fathom.video/share/sjixPQRjNdz9eUP4Zi9ePpMP9iaftgQB',
      source: 'fathom',
      metadata: { thumbnail: '/images/recordings/bay-path-demo.jpg', duration: '40 min' },
    },
    {
      id: 'recording-july-21',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Bay Path and Prentus follow-up',
      description: 'July 21, 2026. Follow-up on Bay Path priorities, stakeholders, and the path to a broader partnership.',
      url: 'https://fathom.video/share/xyRRUSfMFN23pRDVnM85g8zzt_fr3Gd9',
      source: 'fathom',
      metadata: { thumbnail: '/images/recordings/bay-path-follow-up.jpg' },
    },
    {
      id: 'recording-august-13',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Bay Path and Prentus review',
      description: 'August 13, 2026. Review with the wider Bay Path team covering AI, outcomes, employer engagement, and implementation fit.',
      url: 'https://fathom.video/share/wJf-dVweuNi8Sheg6TsFR23UJCmBjuLf',
      source: 'fathom',
      metadata: { thumbnail: '/images/recordings/bay-path-review.jpg' },
    },
    {
      id: 'collateral-journey',
      sectionKey: 'collateral',
      type: 'pdf',
      title: 'The Bay Path Student Journey',
      description: 'A student journey designed around Bay Path’s online and adult students.',
      url: '/bay-path-student-journey.png',
      source: 'generated',
      metadata: { thumbnail: '/bay-path-student-journey.png' },
    },
    {
      id: 'collateral-handshake',
      sectionKey: 'collateral',
      type: 'pdf',
      title: 'Prentus + Handshake, Together',
      description: 'How Prentus adds proactive engagement and outcomes visibility around Handshake.',
      url: '/bay-path-handshake.png',
      source: 'generated',
      metadata: { thumbnail: '/bay-path-handshake.png' },
    },
    ...STANDARD_PRODUCT_ASSETS,
    ...STANDARD_CASE_STUDY_ASSETS,
  ],
  calculatorInputs: {
    institutionType: 'private',
    enrolled: 5_000,
    revenuePerStudent: 24_000,
    attritionPercent: 18,
    retentionLiftPercent: 2,
    advisors: 4,
    studentsServed: 5_000,
    implementationScope: 1,
    termYears: 1,
  },
  quotes: [
    {
      id: 'quote-1',
      quoteNumber: 'PQ-20260815-7K3M9Q',
      fileName: 'Prentus-Bay-Path-pricing-proposal.pdf',
      validUntil: '2026-09-14',
      createdAt: '2026-08-15T16:05:00.000Z',
      downloadUrl: '/api/workspaces/bay-path-preview/quotes/quote-1',
    },
    {
      id: 'quote-2',
      quoteNumber: 'PQ-20260817-4X8N2R',
      fileName: 'Prentus-Bay-Path-pricing-proposal.pdf',
      validUntil: '2026-09-16',
      createdAt: '2026-08-17T19:30:00.000Z',
      downloadUrl: '/api/workspaces/bay-path-preview/quotes/quote-2',
    },
  ],
  viewerEmail: 'preview@baypath.edu',
  updatedAt: '2026-08-17T17:30:00.000Z',
}

export const BAY_PATH_PUBLIC_WORKSPACE = publicWorkspace

export const BAY_PATH_ADMIN_WORKSPACE: AdminWorkspace = {
  ...publicWorkspace,
  status: 'published',
  companyHubspotId: 'bay-path-preview',
  deals: [{ id: 'bay-path-preview', name: 'Bay Path University Partnership', stage: 'Proposal' }],
  allowedDomains: ['baypath.edu'],
  pricingVisibility: 'visible',
  pendingApprovals: 1,
  suggestions: [],
  sandboxRequests: [],
  qualification: null,
  followUp: null,
  reviewWarnings: [],
  members: publicWorkspace.members.map((member) => ({
    ...member,
    stakeholderRole: member.id === 'member-peter' ? 'champion' : member.id === 'member-frank' ? 'influencer' : null,
  })),
  tasks: publicWorkspace.tasks.map((task) => ({ ...task, visibility: 'published' })),
  assets: [...publicWorkspace.assets.map((asset) => ({ ...asset, visibility: 'published' as const })), {
    ...BAY_PATH_PENDING_COLLATERAL,
    visibility: 'pending' as const,
  }],
  businessCase: publicWorkspace.businessCase.map((point) => ({ ...point, visibility: 'published', sourceRefs: [] })),
  timeline: [
    {
      id: 'timeline-call-1',
      source: 'fathom',
      kind: 'meeting',
      title: 'Bay Path University and Prentus demo',
      occurredAt: '2026-06-11T18:00:00.000Z',
      summary: 'Peter shared Bay Path’s need to move career services from passive support toward proactive, scalable engagement for a larger online and adult-student population.',
      body: null,
      sourceUrl: 'https://fathom.video/share/sjixPQRjNdz9eUP4Zi9ePpMP9iaftgQB',
      participants: ['Peter Testori', 'Rod Danan'],
    },
    {
      id: 'timeline-call-2',
      source: 'fathom',
      kind: 'meeting',
      title: 'Bay Path and Prentus follow-up',
      occurredAt: '2026-07-21T18:00:05.000Z',
      summary: 'Peter and Rod aligned on Bay Path priorities, the stakeholders needed for evaluation, and how Prentus could support a broader career outcomes transformation.',
      body: null,
      sourceUrl: 'https://fathom.video/share/xyRRUSfMFN23pRDVnM85g8zzt_fr3Gd9',
      participants: ['Peter Testori', 'Rod Danan'],
    },
    {
      id: 'timeline-call-3',
      source: 'fathom',
      kind: 'meeting',
      title: 'Bay Path and Prentus review',
      occurredAt: '2026-08-13T19:15:25.000Z',
      summary: 'The wider Bay Path team reviewed AI career guidance, outcome tracking, employer engagement, current systems, and implementation priorities.',
      body: null,
      sourceUrl: 'https://fathom.video/share/wJf-dVweuNi8Sheg6TsFR23UJCmBjuLf',
      participants: ['Peter Testori', 'Frank Rojas', 'Natalia Blank', 'Rod Danan'],
    },
    {
      id: 'timeline-email-1',
      source: 'gmail',
      kind: 'email',
      title: 'Bay Path and Prentus follow up',
      occurredAt: '2026-06-12T14:30:00.000Z',
      summary: 'Follow-up sent with the recording, Bay Path-specific collateral, and a promise to prepare tailored pricing.',
      body: 'Thanks again for the conversation. I pulled together a Bay Path-specific overview and the call recording so your team can review everything in one place.',
      sourceUrl: null,
      participants: ['Peter Testori', 'Rod Danan'],
    },
  ],
}
